/**
 * Postfach-Zuordnung — WP-D2 (Phase C.2), extrahiert aus `pages/posteingang.tsx` (dort seit
 * PR #949 "Postfach P3" gebaut, aber page-lokal). Antoine, 09.09.2026: "Ich muss aber auch
 * überall die Möglichkeit haben eine E-Mail einem Vorgang zu ordnen zu können. Das fehlt mir noch
 * diese komplette Dynamik." — diese drei Bausteine sind der wiederverwendbare Kern dafür, jetzt
 * auch von `pages/event-detail.tsx` aus nutzbar, nicht mehr nur vom Posteingang.
 *
 * Reine Extraktion, KEINE Verhaltensänderung: `ZuordnungAendernModal`, `ZuordnungAufhebenModal`
 * und `ZuordnungsChip` funktionieren exakt wie vorher — dieselben Routen
 * (`POST /api/messages/:id/reassign`, `GET /api/messages/:id/zuordnungen`), dieselben Texte,
 * dieselbe Optik. `posteingang.tsx`s bestehende Tests (`posteingang-zuordnung.test.tsx`,
 * `posteingang-zuordnung-aendern.test.tsx`) decken das unverändert ab.
 */
import { useEffect, useState } from "react";
import { anlassAnzeige } from "./format";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Icon, Modal, Drawer, ErrorNote, useToast, Input } from "./components";
import { VorgangsPicker, type VorgangsPickerTreffer } from "./VorgangsPicker";
import { SIGNAL_CHIP_TEXT, zuordnungsArtLabel, zuordnungsAktionLabel, alsSignalCodes } from "../lib/zuordnungs-begruendung";

/** WP-D2: eine Zeile aus `GET /api/messages/:id/zuordnungen` (`message_assignment_log`,
 *  Migration 388) — Basis des Chips über der Bubble und des Warum-Panels im Drawer. */
export interface ZuordnungsEintrag {
  id: string;
  fromEventId: string | null;
  toEventId: string | null;
  actorType: string;
  actorUserId: string | null;
  signals: unknown;
  reasonCode: string;
  reasonText: string | null;
  createdAt: string;
}

/** Dasselbe kompakte Datumsformat wie `pages/posteingang.tsx`s lokales `formatDatum` — eigene,
 *  schlanke Kopie statt eines Imports quer über zwei Module (dieselbe Konvention wie
 *  `lib/zuordnungs-begruendung.ts`s Kopfkommentar sie für die Signal-Tabellen begründet). */
function formatDatum(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export interface ZuordnungAendernModalProps {
  open: boolean;
  onClose: () => void;
  messageId: string;
  aktuellerEventId: string;
  onErfolg: () => void;
}

/**
 * WP-D2: Modal „Zuordnung ändern…" — `VorgangsPicker` + optionales Grund-Feld,
 * `POST /api/messages/:id/reassign`. Der aktuelle Vorgang wird aus dem Picker ausgeschlossen (ein
 * Wechsel auf denselben Vorgang wäre `unchanged`/409, ohne sichtbaren Nutzen). Titel/Knopf tragen
 * bewusst unterschiedliche Texte ("Zuordnung ändern" / "Übernehmen") — sonst träfen zwei Elemente
 * mit demselben zugänglichen Namen aufeinander (Button vs. Überschrift).
 */
export function ZuordnungAendernModal({ open, onClose, messageId, aktuellerEventId, onErfolg }: ZuordnungAendernModalProps) {
  const [gewaehlt, setGewaehlt] = useState<VorgangsPickerTreffer | null>(null);
  const [grund, setGrund] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (open) {
      setGewaehlt(null);
      setGrund("");
      setFehler(null);
      setBusy(false);
    }
  }, [open]);

  async function bestaetigen() {
    if (!gewaehlt) return;
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/messages/${messageId}/reassign`, "POST", {
        eventId: gewaehlt.id,
        ...(grund.trim() ? { reasonText: grund.trim() } : {}),
      });
      toast.success(`Nachricht dem Vorgang „${anlassAnzeige(gewaehlt.occasion) ?? "Vorgang"}“ zugeordnet`);
      onErfolg();
      onClose();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Zuordnung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="560px"
      title="Zuordnung ändern"
      subtitle="Diese Nachricht wird einem anderen Vorgang zugeordnet — der aktuelle Vorgang bleibt für alle anderen Nachrichten unverändert."
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" onClick={() => void bestaetigen()} disabled={busy || !gewaehlt}>
            {busy ? "Wird zugeordnet…" : "Übernehmen"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <VorgangsPicker onSelect={setGewaehlt} ausgeschlossen={[aktuellerEventId]} nurAktive />
        {gewaehlt && (
          <p className="rounded-lg bg-surface-container-high/60 px-3 py-2 font-body-sm text-[12.5px] text-on-background dark:bg-white/[0.03] dark:text-inverse-on-surface">
            Ausgewählt: <strong>{anlassAnzeige(gewaehlt.occasion) ?? "Vorgang"}</strong>
          </p>
        )}
        <label className="block">
          <span className="mb-1 block font-label-md text-[10.5px] uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim">Grund (optional)</span>
          <Input
            type="text"
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            maxLength={300}
            placeholder="z. B. Falsch zugeordnet — gehört zu Vorgang XY"
          />
        </label>
        {fehler && <p className="font-body-sm text-[12px] text-error">{fehler}</p>}
      </div>
    </Modal>
  );
}

export interface ZuordnungAufhebenModalProps {
  open: boolean;
  onClose: () => void;
  messageId: string;
  onErfolg: () => void;
}

/**
 * WP-D2: Bestätigungs-Modal „Zuordnung aufheben" — `POST /api/messages/:id/reassign` mit
 * `eventId: null`. Bewusst OHNE `ConfirmSlugModal`-Texteingabe (die ist fürs Unwiderrufliche
 * reserviert, s. `customers.tsx`s Zusammenführen-Dialog): Ent-Zuordnung ist reversibel — die
 * Nachricht landet nur wieder unter „Ohne Vorgang" und kann von dort erneut zugeordnet werden.
 */
export function ZuordnungAufhebenModal({ open, onClose, messageId, onErfolg }: ZuordnungAufhebenModalProps) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (open) {
      setBusy(false);
      setFehler(null);
    }
  }, [open]);

  async function bestaetigen() {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/messages/${messageId}/reassign`, "POST", { eventId: null });
      toast.success("Zuordnung aufgehoben — die Nachricht liegt wieder unter „Ohne Vorgang“.");
      onErfolg();
      onClose();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Aufheben fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="440px"
      title="Zuordnung aufheben?"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" onClick={() => void bestaetigen()} disabled={busy}>
            {busy ? "Wird aufgehoben…" : "Aufheben"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
          Die Nachricht wandert zurück unter „Ohne Vorgang“ im Posteingang und kann von dort erneut zugeordnet werden.
        </p>
        {fehler && <ErrorNote message={fehler} />}
      </div>
    </Modal>
  );
}

export interface ZuordnungsChipProps {
  messageId: string;
}

/**
 * WP-D2: Chip „Zugeordnet: manuell/automatisch/Vorschlag bestätigt" — Klick öffnet den Drawer mit
 * `GET /api/messages/:id/zuordnungen` als Zeitleiste (das „Warum-Panel je Nachricht"). Lädt die
 * Route direkt beim Rendern der Bubble (kein Cache, dieselbe bewusste Einfachheit wie `useApi`
 * überall sonst — ein Vorgangs-Thread hat typischerweise wenige bis einige Dutzend Nachrichten,
 * kein Grund für eine zweite Ladeinfrastruktur). Fehlt der Audit-Log komplett (Nachrichten, die vor
 * Migration 388 zugeordnet wurden), zeigt der Chip nur "Zugeordnet" ohne Detail, der Drawer einen
 * Hinweis statt einer leeren Liste.
 */
export function ZuordnungsChip({ messageId }: ZuordnungsChipProps) {
  const { data } = useApi<ZuordnungsEintrag[]>(`/api/messages/${messageId}/zuordnungen`);
  const [drawerOffen, setDrawerOffen] = useState(false);
  const eintraege = data ?? [];
  const letzte = eintraege.length > 0 ? eintraege[eintraege.length - 1]! : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setDrawerOffen(true)}
        className="flex items-center gap-1 rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10px] font-semibold text-secondary hover:bg-surface-container-high/70 dark:bg-white/[0.06] dark:text-secondary-fixed-dim"
      >
        <Icon name="history" className="text-[11px]" />
        Zugeordnet{letzte ? `: ${zuordnungsArtLabel(letzte)}` : ""}
      </button>
      <Drawer open={drawerOffen} onClose={() => setDrawerOffen(false)} title="Zuordnungs-Verlauf" breite="schmal">
        {eintraege.length === 0 ? (
          <p className="font-body-sm text-[13px] text-on-surface-variant">
            Kein Zuordnungs-Verlauf vorhanden — diese Nachricht wurde vor Einführung des Protokolls zugeordnet.
          </p>
        ) : (
          <ul className="space-y-3">
            {eintraege.map((e) => {
              const signale = alsSignalCodes(e.signals);
              return (
                <li key={e.id} className="rounded-xl border border-outline-variant/40 p-3 dark:border-outline/30">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-body-sm text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{zuordnungsArtLabel(e)}</span>
                    <span className="font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">{formatDatum(e.createdAt)}</span>
                  </div>
                  <p className="mt-1 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">{zuordnungsAktionLabel(e)}</p>
                  {signale.length > 0 && (
                    <ul className="mt-1.5 flex flex-wrap gap-1">
                      {signale.map((s) => (
                        <li key={s} className="rounded-full bg-surface-container-high px-1.5 py-0.5 font-label-md text-[9.5px] text-secondary dark:bg-white/[0.06] dark:text-secondary-fixed-dim">
                          {SIGNAL_CHIP_TEXT[s]}
                        </li>
                      ))}
                    </ul>
                  )}
                  {e.reasonText && <p className="mt-1.5 font-body-sm text-[12px] italic text-on-surface-variant">„{e.reasonText}“</p>}
                </li>
              );
            })}
          </ul>
        )}
      </Drawer>
    </>
  );
}
